import { uniq } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { trackResetFilters } from '../../../services/analytics';
import {
  selectFilters,
  selectTopics,
  setFilters,
  useAppDispatch,
} from '../../../store';
import { IMentor, Topic } from '../../../types';
import { useTopicFilter } from '../../shared/filters';
import useGetEvents from '../../shared/hook/use-get-events';

const useMentorPageFilters = () => {
  const { t, i18n } = useTranslation();
  const { topics } = useSelector(selectTopics);
  const filters = useSelector(selectFilters);
  const [language, setLanguage] = useState<string | number>(0);
  const [languageLoading, setLanguageLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { oneOnOne, isLoading } = useGetEvents();

  const mentorLanguages = new Set(
    oneOnOne.flatMap((mentor: any) => mentor.details?.languages || []),
  );

  const handleChangeLanguage = (event: any) => {
    const languageCode = event.target.value
      ? event.target.value.slice(0, -1)
      : undefined;
    setLanguageLoading(true);

    setTimeout(() => {
      setLanguage(event.target.value);
      dispatch(setFilters({ ...filters, language: languageCode }));
      setLanguageLoading(false);
    }, 500);
  };

  const getLaguageFilterOptions = (languages: any) => {
    const allLanguages = {
      id: 'allLanguages',
      value: 0,
      name: t('v2.global.filterChip_label_allLanguages'),
    };

    const options: { id: string; value: number; name: string }[] = [];

    languages.forEach((language: string, index: number) => {
      const languageLabel = new Intl.DisplayNames([i18n.language], {
        type: 'language',
      });
      options.push({
        id: language,
        value: index + 1,
        name: `${language && languageLabel.of(language)}`,
      });
    });

    return [
      allLanguages,
      ...options.sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }),
    ];
  };

  const selectedLanguageCode = getLaguageFilterOptions(mentorLanguages).find(
    (option: any) => option.value === language,
  )!.id;

  const mentorTopics = uniq(
    oneOnOne.flatMap((mentor: any) => mentor?.focusTopics),
  );
  const availableTopics = topics.filter(({ key }: Topic) =>
    mentorTopics.includes(key),
  );
  const { handleChangeTopic, topic, filterByTopic, loading } = useTopicFilter({
    availableTopics,
  });

  const filteredMentors = filterByTopic(
    oneOnOne,
    'focusTopics',
  ).filter((mentor) =>
    [...mentor.languages, 'allLanguages'].includes(selectedLanguageCode),
  );

  const recommendedMentorsList = filteredMentors?.filter(
    ({ isRecommended }: IMentor) => isRecommended,
  );
  const notRecommendedMentorsList = filteredMentors?.filter(
    ({ isRecommended }: IMentor) => !isRecommended,
  );

  const handleReset = () => {
    setLanguage(0);

    trackResetFilters({ location: 'mentors' });
  };

  return {
    handleChangeLanguage,
    handleChangeTopic,
    recommendedMentorsList,
    notRecommendedMentorsList,
    languageOptions: getLaguageFilterOptions(mentorLanguages),
    language,
    topic,
    availableTopics,
    handleReset,
    showReset: !filteredMentors.length,
    loading: loading || languageLoading || isLoading,
  };
};

export default useMentorPageFilters;
